import { FC } from 'react';
import {
  HowToSection,
  LabelExplanation,
  LeftSide,
  ListWrapper,
  LoaderWrapper,
  Step,
  Subtitle,
  Wrapper
} from './styles';
import SectionTitle from '@anm/components/video-landings/SectionTitle';
import { TabInfoWrapper } from '../../hosting/CustomizationSection/tabs/EmbedTab/EmbedInfo/Wrapper';
import AddDomain from './AddDomain';
import DomainsList from './List';
import { ShareBlock, ShareBlockDescription } from '../../hosting/CustomizationSection/tabs/LandingTab/Wrapper';
import CopyLink from '@anm/components/CopyLink';
import useFetchDomains from './hooks/useFetchDomains';
import { useDispatch, useSelector } from 'react-redux';
import { landingsActions, landingsSelectors } from '../../../store/modules/landings';
import config from '../../../config';
import { userSelectors } from '../../../store/modules/user';
import { StatusLabel } from './List/styles';
import { Features } from '@anm/shared/features';
import { selectWaveSubscription } from '../../../store/modules/user/selectors';

import { isProd } from '@anm/shared/mode';

type DomainsTabProps = {
  hostId: string;
  isWebinar?: boolean;
};

const CNAME = isProd ? `custom.wave.video` : `custom-domains.test.stacks.animatron-test.com`;

const DomainsTab: FC<DomainsTabProps> = ({ hostId, isWebinar = false }) => {
  const dispatch = useDispatch();
  const page = useSelector(landingsSelectors.selectLanding);
  const [domainsList, isPending] = useFetchDomains();
  const selectedDomain = page?.parameters?.customDomain;
  const domainsUsed = (domainsList || []).length;
  const availableDomainsCount = useSelector(userSelectors.selectCustomDomains);
  const canSetDomains = useSelector(userSelectors.selectCanSetDomains);
  const canAddDomains = canSetDomains && domainsUsed < availableDomainsCount;
  const canUseCustomDomains = Features.canUseCustomDomains(useSelector(selectWaveSubscription));
  const defaultUrl = isWebinar ? `${config().urls.webinarUrl}${hostId}` : `${config().urls.videoLanding}${hostId}`;
  const prefix = isWebinar ? 'w' : 'v';
  const pageUrl = selectedDomain ? `https://${selectedDomain}/${prefix}${hostId}` : defaultUrl;

  const selectDomain = (domain: string) => {
    if (!canSetDomains) return;

    dispatch(
      landingsActions.updateLanding({
        parameters: { ...page?.parameters, customDomain: domain }
      })
    );
  };

  if (!canUseCustomDomains) return null;

  return (
    <Wrapper>
      {isPending ? (
        <LoaderWrapper size="large" color="blue" />
      ) : (
        <>
          <LeftSide>
            <SectionTitle title="Create Custom Domain" level={2} />
            <TabInfoWrapper variant="normal">
              You are currently using {domainsUsed} out of {availableDomainsCount} custom domains
            </TabInfoWrapper>
            <AddDomain disabled={!canSetDomains || !canAddDomains} />
            {domainsList && <DomainsList {...{ selectedDomain, selectDomain }} list={domainsList}></DomainsList>}
            <ShareBlock>
              <CopyLink
                link={pageUrl}
                buttonSize="medium"
                buttonText="Copy link"
                inputVariant="disabled_gray"
                buttonVariant="white-blue-border-share"
                isFieldClickable={true}
              />
            </ShareBlock>
            <ShareBlockDescription>
              Only people with this link can see your {isWebinar ? 'webinar' : 'landing'} page, it's not listed
              anywhere.
            </ShareBlockDescription>
          </LeftSide>
          <HowToSection>
            <Subtitle>How to configure custom domain</Subtitle>
            <ListWrapper>
              <Step>
                The first step is to sign into your DNS provider. This could be your domain registrar (like GoDaddy,
                Namecheap, etc.) or it could be a DNS management service if you use one (like Cloudflare, AWS Route 53,
                etc.).
              </Step>
              <Step>
                After you've logged in, you need to locate your DNS management area. This is usually labeled as "DNS
                Management," "DNS Settings," "Name Server Management," or something similar.
              </Step>
              <Step>Within the DNS management page, find the section where you can add new records.</Step>
              <Step>
                You'll want to add a new record, and specify that it is a CNAME type. In the 'Host' or 'Name' field, you
                should enter your subdomain. In the 'Value' or 'Points to' field, enter <b>{CNAME}</b>.
              </Step>
              <Step>Save your changes.</Step>
              <Step>
                DNS changes can take up to 24-48 hours to propagate across the entire internet, though often it's much
                quicker. During this time, some users might still be directed to the old IP address while others are
                directed to the new one.
              </Step>
            </ListWrapper>
            <LabelExplanation>
              <StatusLabel status="INIT">verifying</StatusLabel>
              <p>Domain added, but not ready to use yet</p>
            </LabelExplanation>
            <LabelExplanation>
              <StatusLabel status="OK">verified</StatusLabel>
              <p>Domain is added and ready to use</p>
            </LabelExplanation>
          </HowToSection>
        </>
      )}
    </Wrapper>
  );
};

export default DomainsTab;
